/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 25-08-2026
 *
 * Het tweede docentendashboard (golf 3g): een raster van studentmodellen, per model een tijdbalk
 * met de acties als tikjes en twee lijnen over een instelbare periode. Vervangt de
 * `dashboardv2`-directive uit ng1.
 *
 * De opgehaalde modellen en hun actiehistorie komen uit de `Dashboardv2Service`; alles wat je
 * hier kunt instellen — de periode, het filter, het raster, de sortering en de live-schakelaar —
 * is state van dit paneel en leeft dus hier, op één sessionStorage-sleutel zodat het een reload
 * overleeft.
 *
 * Twee afleidingen zijn bewust géén computed. De **periode** wordt pas toegepast als de docent
 * "Bijwerken" of een van de resetknoppen indrukt, precies zoals ng1's `setDatestrings`; typen in
 * een datumveld verandert het beeld dus nog niet. En de **volgorde** wordt alleen herrekend op
 * een sorteerknop of na een afgeronde haalronde — een computed zou bij elke binnenkomende actie
 * hersorteren en dan springen de blokken onder de handen van de docent rond. Het tekstfilter en
 * de live-filter zijn wél computeds: die veranderen alleen door een handeling van de docent zelf
 * of tonen/verbergen zonder de volgorde te raken.
 */
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    type OnDestroy,
    signal,
    untracked
} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {TranslatePipe} from '@ngx-translate/core';
import {DateTime} from 'luxon';
import {KeyedTimeout, Sihwdatumveld} from 'sihw-ngx';
import {BaseIslandComponent} from '../base-island.component';
import {
    type Dashboardv2Actiedata,
    type Dashboardv2Model,
    Dashboardv2Service,
    type Dashboardv2Telling
} from '../../services/dashboardv2.service';
import {SessieService} from '../../services/sessie.service';
import {ToastService} from '../../services/toast.service';
import {ModelbeheerService} from '../../app/model/modelbeheer.service';
import {DlModelactieComponent, type Dashboardv2Periode} from './dl-modelactie.component';

/**
 * Waarop het raster gesorteerd kan worden.
 */
export type Dashboardv2Sortering = 'az' | 'aantal' | 'fout' | 'begin' | 'eind';

/**
 * De instellingen van het paneel, zoals ze in de sessie bewaard worden.
 */
export interface Dashboardv2Form {
    /** Hoeveel grafieken er naast elkaar staan. */
    grid: number;
    /** Waarop het raster gesorteerd is. */
    sorteer: Dashboardv2Sortering;
    /** Tekst waarop de modellen gefilterd worden. */
    filter: string;
    /** Of alleen de modellen getoond worden die op dit moment openstaan. */
    live: boolean;
    /** Het begin van de periode als sql-date, of `null` voor "nu". */
    startdate: string | null;
    /** Het begin van de periode als `HH:mm`. */
    starttime: string | null;
    /** Het einde van de periode als sql-date, of `null` voor "over twee uur". */
    enddate: string | null;
    /** Het einde van de periode als `HH:mm`. */
    endtime: string | null;
}

/**
 * Eén rij in het raster: het model met de gegevens die erbij horen.
 */
interface Dashboardv2Rij {
    model: Dashboardv2Model;
    data: Dashboardv2Actiedata;
}

/**
 * De sleutel waaronder de instellingen in de sessie staan; dezelfde als in ng1, zodat een
 * lopende sessie zijn instellingen houdt.
 */
const OPSLAGSLEUTEL = 'dashboardv2';

/**
 * De standaardinstellingen.
 */
const STANDAARDFORM: Dashboardv2Form = {
    grid: 2,
    sorteer: 'az',
    filter: '',
    live: false,
    startdate: null,
    starttime: null,
    enddate: null,
    endtime: null
};

/**
 * Hoe vaak de klok tikt die de lijnen naar nu doortrekt.
 */
const KLOKTIK = 60000;

/**
 * Hoe lang het filterveld wacht voordat het zijn tekst doorzet.
 */
const FILTERVERTRAGING = 800;

/**
 * Een model zonder gegevens; als vaste waarde, zodat het raster niet bij elke hertekening een
 * nieuw leeg object aan zijn children geeft.
 */
const LEGE_DATA: Dashboardv2Actiedata = {acties: [], fouten: []};

/**
 * Sorteerwaarde voor een model zonder eerste actie: die staan achteraan bij het oplopend
 * sorteren op begintijd.
 */
const GEEN_BEGIN = Number.MAX_SAFE_INTEGER;

/**
 * Sorteerwaarde voor een model zonder laatste actie: die staan achteraan bij het aflopend
 * sorteren op eindtijd.
 */
const GEEN_EIND = -1;

@Component({
    selector: 'dl-dashboardv2',
    standalone: true,
    templateUrl: './dl-dashboardv2.component.html',
    styleUrls: ['./dl-dashboardv2.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [DlModelactieComponent, FormsModule, Sihwdatumveld, TranslatePipe],
    host: {
        '[class.dl-dashboardv2--open]': 'dashboard.open()'
    }
})
export class DlDashboardv2Component extends BaseIslandComponent implements OnDestroy {

    /**
     * De sessie (golf a1): of de gebruiker met een model mag meekijken — dat bepaalt of een klik
     * op een blok ergens toe leidt — en zijn weergavenaam. Het backend bouwt de `user` van een
     * modelrij met dezelfde uitdrukking (`displaynaam || username`), dus aan die naam is te zien
     * welke modellen van de gebruiker zelf zijn; die open je gewoon in plaats van dat je ermee
     * meekijkt.
     */
    private readonly sessie = inject(SessieService);

    /**
     * De open-state en de opgehaalde gegevens.
     */
    protected readonly dashboard = inject(Dashboardv2Service);

    /**
     * Het modelbeheer, dat een aangeklikt model opent (golf 4c).
     */
    private readonly modelbeheer = inject(ModelbeheerService);

    /**
     * De meldingen rechtsonder, voor een model dat niet te openen is.
     */
    private readonly toast = inject(ToastService);

    /**
     * De instellingen van het paneel.
     */
    protected readonly form = signal<Dashboardv2Form>(leesForm());

    /**
     * Het tijdvak dat getoond wordt.
     */
    protected readonly periode = signal<Dashboardv2Periode>({start: DateTime.now(), eind: DateTime.now()});

    /**
     * Het huidige moment, bijgewerkt door de klok. Eén klok voor het hele paneel; ng1 had er één
     * per model.
     */
    protected readonly nu = signal(Date.now());

    /**
     * De model-id's in de volgorde waarin ze getoond worden.
     */
    private readonly volgorde = signal<readonly string[]>([]);

    /**
     * De klok die de lijnen naar nu doortrekt; loopt alleen zolang het paneel openstaat.
     */
    private klok: ReturnType<typeof setInterval> | null = null;

    /**
     * De vertraging op het filterveld.
     */
    private readonly filtertimer = new KeyedTimeout();

    /**
     * De rasterkeuze als tekst, zoals de keuzelijst hem verwacht.
     */
    protected readonly gridKeuze = computed(() => String(this.form().grid));

    /**
     * De rijen van het raster: de gesorteerde modellen die het filter overhouden, met hun
     * gegevens erbij.
     */
    protected readonly rijen = computed<Dashboardv2Rij[]>(() => {
        const perId = new Map(this.dashboard.modellen().map(model => [model.id, model]));
        const actiedata = this.dashboard.actiedata();
        const form = this.form();
        const filter = form.filter.toLowerCase();
        return this.volgorde()
            .map(id => perId.get(id))
            .filter((model): model is Dashboardv2Model => !!model
                && (!filter.length || `${model.user} - ${model.titel}`.toLowerCase().includes(filter))
                && (!form.live || model.live))
            .map(model => ({model, data: actiedata[model.id] ?? LEGE_DATA}));
    });

    constructor() {
        super();
        //het paneel gaat open: verse periode, verse haalronde en de klok aan
        effect(() => {
            const open = this.dashboard.open();
            untracked(() => open ? this.begin() : this.stopKlok());
        });
        //een afgeronde haalronde is het moment om de startdatum en de volgorde bij te trekken
        effect(() => {
            this.dashboard.haalronde();
            untracked(() => {
                this.trekStartdatumOp();
                this.hersorteer();
            });
        });
        //instellingen bewaren, zodat ze een reload overleven
        effect(() => sessionStorage.setItem(OPSLAGSLEUTEL, JSON.stringify(this.form())));
    }

    /**
     * Het eiland verdwijnt (state-wissel, uitloggen): dan hoeft het backend ons ook niets meer te
     * sturen. De service is een root-singleton en zou anders geabonneerd blijven.
     */
    ngOnDestroy(): void {
        this.stopKlok();
        this.filtertimer.clearAll();
        this.dashboard.sluiten();
    }

    /**
     * Sluit het paneel.
     */
    protected sluitPaneel(): void {
        this.dashboard.sluiten();
    }

    /**
     * Zet een of meer instellingen.
     */
    protected zetForm(deel: Partial<Dashboardv2Form>): void {
        this.form.update(form => ({...form, ...deel}));
    }

    /**
     * De docent drukt op "Bijwerken": de ingevulde periode gaat gelden en we halen op wat er
     * binnen die grens relevant is. Modellen die we al kennen houden hun gegevens.
     */
    protected bijwerken(): void {
        this.pasPeriodeToe();
        void this.dashboard.haalOpnieuw(false);
    }

    /**
     * Toon alles: vanaf ver voor het begin van het project tot overmorgen. De startdatum wordt
     * daarna opgetrokken naar het oudste model.
     */
    protected resetAlles(): void {
        this.zetForm({
            startdate: '1999-12-31',
            starttime: '23:59',
            enddate: DateTime.now().plus({days: 2}).toISODate(),
            endtime: '00:00'
        });
        this.pasPeriodeToe();
        void this.dashboard.haalOpnieuw(true);
    }

    /**
     * Toon de voortgang vanaf nu, twee uur vooruit.
     */
    protected resetNu(): void {
        const nu = DateTime.now();
        const einde = nu.plus({hours: 2});
        this.zetForm({
            startdate: nu.toISODate(),
            starttime: nu.toFormat('HH:mm'),
            enddate: einde.toISODate(),
            endtime: einde.toFormat('HH:mm')
        });
        this.pasPeriodeToe();
        void this.dashboard.haalOpnieuw(true);
    }

    /**
     * Wisselt de live-schakelaar om.
     */
    protected toggleLive(): void {
        this.zetForm({live: !this.form().live});
    }

    /**
     * Zet het aantal grafieken naast elkaar. De keuzelijst levert een string; hier wordt het weer
     * het getal dat de rest van het paneel verwacht.
     */
    protected zetGrid(waarde: string): void {
        this.zetForm({grid: Number(waarde)});
    }

    /**
     * Zet de sortering en herschik het raster.
     */
    protected zetSortering(sorteer: Dashboardv2Sortering): void {
        this.zetForm({sorteer});
        this.hersorteer();
    }

    /**
     * De docent typt in het filterveld; de tekst gaat pas na een korte stilte gelden.
     */
    protected planFilter(tekst: string): void {
        this.filtertimer.set('filter', () => this.zetForm({filter: tekst}), FILTERVERTRAGING);
    }

    /**
     * Wist het filter meteen.
     */
    protected wisFilter(veld: HTMLInputElement): void {
        this.filtertimer.clear('filter');
        veld.value = '';
        this.zetForm({filter: ''});
    }

    /**
     * Kijk mee met dit model. Bij een sub gaat het hoofdmodel open; het modelbeheer laadt het
     * model en sluit dit paneel.
     */
    protected meekijken(model: Dashboardv2Model): void {
        if (!this.sessie.magMeekijken()) {
            return;
        }
        const doel = model.rol === 'sub' ? model.hoofdmodel : model.id;
        if (!doel) {
            this.toast.toon('DASHBOARDV2.KANMODELNIETOPENEN', 'error');
            return;
        }
        void this.modelbeheer.meekijken(doel, this.isEigenModel(doel));
    }

    /**
     * Of het model dat opengaat van de gebruiker zelf is; dan is het geen meekijken maar gewoon
     * openen. De vraag geldt het **doel**, niet het aangeklikte blok: bij een sub van een ander
     * kan het hoofdmodel van jezelf zijn. Zit dat doel niet in de opgehaalde lijst — het kan
     * buiten de eindgrens zijn aangemaakt — dan blijft het de meekijk-route.
     */
    private isEigenModel(modelId: string): boolean {
        const naam = this.sessie.displaynaam();
        return !!naam && this.dashboard.modellen().some(model => model.id === modelId && model.user === naam);
    }

    /**
     * Het paneel gaat open: de periode wordt vers bepaald (zonder ingestelde periode is dat "van
     * nu tot over twee uur") en we halen alles opnieuw op.
     */
    private begin(): void {
        if (this.form().startdate) {
            this.pasPeriodeToe();
            void this.dashboard.haalOpnieuw(true);
        } else {
            this.resetNu();
        }
        this.nu.set(Date.now());
        this.startKlok();
    }

    /**
     * Laat de ingevulde periode gelden en meldt de eindgrens aan de service, die hem als
     * parameter van zijn volgende vragen gebruikt. Niets ingevuld betekent "vanaf nu"
     * respectievelijk "tot over twee uur".
     *
     * Staat het eind vóór het begin, dan worden ze verwisseld — en dan gaan de invoervelden mee.
     * ng1 draaide alleen de getoonde periode om en liet de velden omgekeerd staan; dat is
     * dezelfde vraag als bij de startdatum-correctie hieronder, en daar schreef ng1 wél terug:
     * de docent hoort in de controls te zien wat de grafieken laten zien.
     */
    private pasPeriodeToe(): void {
        const form = this.form();
        const start = leesMoment(form.startdate, form.starttime) ?? DateTime.now().startOf('minute');
        const eind = leesMoment(form.enddate, form.endtime) ?? DateTime.now().plus({hours: 2}).startOf('minute');
        const periode = eind < start ? {start: eind, eind: start} : {start, eind};
        if (eind < start) {
            this.zetForm({
                startdate: periode.start.toISODate(),
                starttime: periode.start.toFormat('HH:mm'),
                enddate: periode.eind.toISODate(),
                endtime: periode.eind.toFormat('HH:mm')
            });
        }
        this.periode.set(periode);
        this.dashboard.zetPeriode(periode.eind);
    }

    /**
     * Trekt de startdatum op naar het oudste model als de docent verder terugkijkt dan er
     * historie is. Dat gaat zichtbaar in de invoervelden, want hij hoort te zien dat er niets
     * ouders is.
     */
    private trekStartdatumOp(): void {
        const oudste = this.dashboard.oudsteModel();
        const periode = this.periode();
        if (oudste === null) {
            return;
        }
        const begin = DateTime.fromMillis(oudste).startOf('minute');
        if (periode.start >= begin) {
            return;
        }
        this.zetForm({startdate: begin.toISODate(), starttime: begin.toFormat('HH:mm')});
        this.periode.set({start: begin, eind: periode.eind});
    }

    /**
     * Bepaalt de volgorde van het raster opnieuw. Bij een gelijke stand beslist het alfabet, net
     * als in ng1.
     */
    private hersorteer(): void {
        const actiedata = this.dashboard.actiedata();
        const sorteer = this.form().sorteer;
        const eind = this.periode().eind.toMillis();
        //de laatste meting respectievelijk actie bínnen het tijdvak, per model
        const laatsteTelling: Record<string, Dashboardv2Telling | undefined> = {};
        const laatsteActie: Record<string, number | undefined> = {};
        for (const model of this.dashboard.modellen()) {
            const data = actiedata[model.id];
            laatsteTelling[model.id] = data?.fouten.filter(fout => fout.tijd <= eind).at(-1)?.telling;
            laatsteActie[model.id] = data?.acties.filter(actie => actie.tijd <= eind).at(-1)?.tijd;
        }
        const gesorteerd = [...this.dashboard.modellen()].sort((links, rechts) => {
            let volgorde = 0;
            switch (sorteer) {
                case 'begin':
                    //op de eerste actie, oplopend
                    volgorde = (actiedata[links.id]?.acties[0]?.tijd ?? GEEN_BEGIN)
                        - (actiedata[rechts.id]?.acties[0]?.tijd ?? GEEN_BEGIN);
                    break;
                case 'eind':
                    //op de laatste actie binnen het tijdvak, aflopend
                    volgorde = (laatsteActie[rechts.id] ?? GEEN_EIND) - (laatsteActie[links.id] ?? GEEN_EIND);
                    break;
                case 'aantal':
                    volgorde = (laatsteTelling[rechts.id]?.aantal ?? 0) - (laatsteTelling[links.id]?.aantal ?? 0);
                    break;
                case 'fout':
                    volgorde = (laatsteTelling[rechts.id]?.fout ?? 0) - (laatsteTelling[links.id]?.fout ?? 0);
                    break;
                case 'az':
                    break; //alleen het alfabet hieronder
            }
            return volgorde || `${links.user}-${links.titel}`.localeCompare(`${rechts.user}-${rechts.titel}`);
        });
        this.volgorde.set(gesorteerd.map(model => model.id));
    }

    /**
     * Start de klok die de twee lijnen naar nu doortrekt.
     */
    private startKlok(): void {
        this.stopKlok();
        this.klok = setInterval(() => this.nu.set(Date.now()), KLOKTIK);
    }

    /**
     * Zet de klok stil.
     */
    private stopKlok(): void {
        if (this.klok) {
            clearInterval(this.klok);
            this.klok = null;
        }
    }
}

/**
 * Leest een datum-tijdpaar uit de invoervelden; `null` zodra een van beide ontbreekt, want dan
 * geldt de standaardwaarde.
 */
function leesMoment(datum: string | null, tijd: string | null): DateTime | null {
    return (datum && tijd) ? DateTime.fromISO(`${datum}T${tijd}`) : null;
}

/**
 * Leest de bewaarde instellingen uit de sessie. Onbekende sleutels overleven, ontbrekende krijgen
 * hun standaardwaarde, en `grid` wordt een getal — de keuzelijst leverde in ng1 een string. Een
 * corrupte of geblokkeerde opslag is een reëel faalpad en levert gewoon de standaard op.
 */
function leesForm(): Dashboardv2Form {
    try {
        const bewaard = sessionStorage.getItem(OPSLAGSLEUTEL);
        const form = bewaard ? Object.assign({}, STANDAARDFORM, JSON.parse(bewaard)) : {...STANDAARDFORM};
        return {...form, grid: Number(form.grid) || STANDAARDFORM.grid};
    } catch (fout) {
        return {...STANDAARDFORM};
    }
}
